/*
 * NB-2 Rod Sleeve buy box (sections/ra-nb2-buybox.liquid). One state object, one render().
 *
 * Free sleeves depend only on how many sleeves are in the order, and are always the cheapest ones:
 *   free(n) = 0 below firstAt · firstFree below secondAt · from secondAt as `beyond` says:
 *   repeat (default): secondFree per full secondAt sleeves (8 → 2, 16 → 4), never less than firstFree;
 *   cap: secondFree; step: secondFree + 1 per `every` sleeves past secondAt.
 * "Buy 4, Get 1 Free" / "Buy 6, Get 2 Free" (Curtis 2026-10-16). The numbers come from the section settings.
 * This is display only: the store's automatic discounts give the free sleeves at checkout.
 * Every price on the box comes from quote(): each deal row shows quote() of what a tap on it adds and
 * the ticked row the live order, so the row, the Add button, the bar and the FREE stickers agree.
 *
 * Deal card fills the picked length (Curtis 2026-10-16, section setting "deal_fill", on by default):
 * once the shopper taps + on a length (st.pick), a deal tap puts all its sleeves on that length ("8 × 6–7 ft")
 * and the rows fold under "Mix lengths?". 54% of multi-sleeve orders are one length. It does not fill when
 * no length was picked (today's spread), when the picked length is sold out or has fewer in stock than the deal
 * (today's spread), or when the shopper has built a mix of their own on other lengths (their mix is kept and
 * topped up or trimmed on the picked length). target() decides, so the deal rows' prices follow it too.
 *
 * Add to cart: one ra:cart-add event (assets/ra-pdp-cart.js), which posts every line in one request
 * and fires Horizon's cart event; assets/ra-cart-rebuy.js then opens Rebuy's Smart Cart.
 */
(() => {
  if (customElements.get('ra-nb2-buybox')) return;

  const reduced = () => window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class RaNb2Buybox extends HTMLElement {
    connectedCallback() {
      if (this.ready) {
        /* moved or re-inserted (theme editor, another script): disconnectedCallback ran off(), so bind the box again.
           Only the scripted flows (their listeners all go through off()); the gallery's own listeners never came off. */
        if (this.parked) {
          this.parked = false;
          this.findSize();
          const run = { stepper: window.raNb2Stp, percent: window.raNb2Pct, minimal: window.raNb2Min }[this.cfg.flow] || null;
          if (run) this.nb2 = run(this, this.cfg);
        }
        return;
      }
      const raw = this.querySelector('[data-nb2-config]');
      if (!raw) return;
      this.ready = true;
      this.cfg = JSON.parse(raw.textContent);
      /* iOS Safari shows :active (the instant pressed look) only where a touch listener exists */
      this.addEventListener('touchstart', () => {}, { passive: true });
      this.findSize();
      this.setup(this.cfg);
    }

    /* "Find my size" (menu) and "Open Size Guide" (Size Chart row) link to #ra-size-guide on this page. The hash is often
       already set, so no hashchange fires on a repeat tap, and the menu drawer would stay open over the guide: handle the
       tap here. Close the drawer first (its focus trap ends when its closing animation does), then open and centre the
       guide and move focus into it. */
    findSize() {
      if (this.onFind) document.removeEventListener('click', this.onFind);
      this.onFind = (e) => {
        const a = e.target instanceof Element ? e.target.closest('a[href*="#ra-size-guide"]') : null;
        if (!a || e.defaultPrevented || e.button > 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        let url;
        try {
          url = new URL(a.href, window.location.href);
        } catch {
          return;
        }
        const fit = this.querySelector('#ra-size-guide');
        if (url.pathname !== window.location.pathname || !fit) return;
        e.preventDefault();
        if (window.location.hash !== '#ra-size-guide') {
          try {
            window.history.replaceState(window.history.state, '', '#ra-size-guide');
          } catch {
            /* the hash is cosmetic */
          }
        }
        const show = () => {
          if (this.nb2 && this.nb2.openFit) return this.nb2.openFit(true);
          /* the other flows: a <details> */
          fit.open = true;
          try {
            fit.scrollIntoView({ block: 'center', behavior: reduced() ? 'auto' : 'smooth' });
          } catch {
            fit.scrollIntoView();
          }
          const s = fit.querySelector('summary');
          if (s) s.focus({ preventScroll: true });
        };
        const drawer = a.closest('header-drawer') || document.querySelector('header-drawer');
        const menu = drawer && drawer.querySelector('details[open]');
        if (!menu || typeof drawer.close !== 'function') return show();
        drawer.close();
        const t0 = performance.now();
        const wait = () => {
          if (!menu.open || performance.now() - t0 > 900) show();
          else requestAnimationFrame(wait);
        };
        requestAnimationFrame(wait);
      };
      document.addEventListener('click', this.onFind);
    }

    disconnectedCallback() {
      this.io?.disconnect();
      this.ro?.disconnect();
      document.documentElement.classList.remove('ra-nb2-bar-on');
      clearTimeout(this.flashT);
      clearTimeout(this.doneT);
      clearTimeout(this.sayT);
      this.nb2?.off?.();
      if (this.onFind) document.removeEventListener('click', this.onFind);
      this.parked = true;
    }

    setup(cfg) {
      /* "Deal flow: Percent tiers": assets/ra-nb2-pct.js (printed before this file, only in that mode) runs the box; the gallery stays here */
      if (cfg.flow === 'percent') {
        this.gallery();
        if (window.raNb2Pct) this.nb2 = window.raNb2Pct(this, cfg);
        return;
      }
      /* "Deal flow: Minimal (FL-5)": assets/ra-nb2-min.js (printed before this file, only in that mode) runs the box */
      if (cfg.flow === 'minimal') {
        this.gallery();
        if (window.raNb2Min) this.nb2 = window.raNb2Min(this, cfg);
        return;
      }
      /* "Deal flow: Stepper (V3-top)": assets/ra-nb2-stp.js (printed before this file, only in that mode) runs the box */
      if (cfg.flow === 'stepper') {
        this.gallery();
        if (window.raNb2Stp) this.nb2 = window.raNb2Stp(this, cfg);
        return;
      }
      const q = (s, c = this) => c.querySelector(s);
      const qa = (s, c = this) => Array.from(c.querySelectorAll(s));
      const fmt = new Intl.NumberFormat('en-US', { style: 'currency', currency: cfg.currency || 'USD' });
      const $ = (cents) => fmt.format(cents / 100);
      const sl = (n) => `${n} ${n === 1 ? 'sleeve' : 'sleeves'}`;

      const S = cfg.sizes;
      const L = S.length;
      const R = cfg.rule;
      const MAX = cfg.max;
      const DEF = Math.min(cfg.def, L - 1);
      const REELS = cfg.reels;
      const CHEAP = S.map((_, i) => i).sort((a, b) => S[a].price - S[b].price || a - b);
      const sum = (c) => c.reduce((a, b) => a + b, 0);
      const free = (n) =>
        n < R.firstAt
          ? 0
          : n < R.secondAt
            ? R.firstFree
            : R.beyond === 'cap'
              ? R.secondFree
              : R.beyond === 'step'
                ? R.secondFree + Math.floor((n - R.secondAt) / Math.max(1, R.every))
                : Math.max(R.firstFree, R.secondFree * Math.floor(n / R.secondAt));
      /* the next count that frees more sleeves (null: none up to the order limit) */
      const nextAt = (n) => {
        for (let m = n + 1; m <= MAX; m++) if (free(m) > free(n)) return m;
        return null;
      };
      /* from here no deal row is ticked and the bar names the order itself: "Buy 12, get 4 free" */
      const BIG = nextAt(R.secondAt) ?? Infinity;
      /* the dots that stand for free sleeves: the last g dots up to each count where g more go free */
      const FREE_DOT = [false];
      for (let i = 1; i <= 16; i++) FREE_DOT[i] = false;
      for (let m = 1; m <= 16; m++) {
        const g = free(m) - free(m - 1);
        for (let j = Math.max(1, m - g + 1); j <= m && g > 0; j++) FREE_DOT[j] = true;
      }

      /* each deal opens with the sleeves spread over the lengths, any extra from the default length on (5 → 1/2/1/1, 8 → 2/2/2/2).
         sections/ra-nb2-buybox.liquid builds the same presets for the first paint: keep both the same. */
      const preset = (n) => {
        const c = S.map(() => 0);
        const ok = S.map((_, i) => i).filter((i) => S[i].available);
        if (!ok.length) return c;
        for (let k = 0; k < n; k++) {
          const i = (DEF + k) % L;
          c[S[i].available ? i : ok[0]]++;
        }
        return c;
      };
      const PRESETS = { [R.firstAt]: preset(R.firstAt), [R.secondAt]: preset(R.secondAt) };
      const isPreset = (c) => Object.values(PRESETS).some((p) => p.join() === c.join());

      const quote = (c) => {
        const n = sum(c);
        const f = free(n);
        const fc = S.map(() => 0);
        let left = f;
        for (const i of CHEAP) {
          if (left <= 0) break;
          fc[i] = Math.min(c[i], left);
          left -= fc[i];
        }
        const full = c.reduce((a, k, i) => a + k * S[i].price, 0);
        const save = fc.reduce((a, k, i) => a + k * S[i].price, 0);
        return { n, f, fc, full, save, total: full - save };
      };
      /* the deal row a count lights: 1 → Single; firstAt–secondAt-1 → deal 2; secondAt–BIG-1 → deal 3; else none */
      const litFor = (n, single) => (n === 0 ? (single ? 1 : 0) : n === 1 ? 1 : free(n) && n < BIG ? (n < R.secondAt ? R.firstAt : R.secondAt) : 0);
      /* the lengths a tap on deal t gives from counts c (chooseDeal applies it) */
      const target = (t, c) => {
        const n = sum(c);
        if (t === 1) return S.map(() => 0); /* Single: the shopper picks the length (Curtis 2026-10-15) */
        const filled = fillOf(t, c); /* "8 × 6–7 ft" */
        if (filled) return filled;
        if (PRESETS[t] && (n <= 1 || isPreset(c))) return PRESETS[t].slice();
        const out = c.slice();
        /* the length extra sleeves go on and come off first: the picked one (fill on, own mix kept), else the default */
        if (pickOk() && t > n) {
          spill(out, t - n, st.pick);
          return out;
        }
        const home = pickOk() ? st.pick : DEF;
        if (t > n) {
          /* the extra sleeves go on that length, or the first length on sale */
          const k = S[home].available ? home : S.findIndex((x) => x.available);
          if (k >= 0) out[k] += t - n;
        } else
          while (sum(out) > t) {
            let k = out[home] > 0 ? home : -1;
            if (k < 0) for (let i = 0; i < L; i++) if (out[i] > 0 && (k < 0 || out[i] > out[k])) k = i;
            out[k]--;
          }
        return out;
      };
      /* ---------- deal card fills the picked length ---------- */
      const FILL = cfg.fill === true;
      /* "Deal flow" (section setting deal_flow): standard, or a free-pick sheet run by assets/ra-nb2-pick.js through this.fp */
      const FLOW = !!cfg.flow && cfg.flow !== 'standard';
      const zeros = () => S.map(() => 0);
      /* the counts on every length but p */
      const rest = (c, p) => c.filter((_, j) => j !== p).join();
      /* the other lengths still hold nothing, or what the page put there (a preset): the shopper has no mix of their own */
      const untouched = (c, p) => c.every((v, j) => j === p || !v) || Object.values(PRESETS).some((x) => rest(x, p) === rest(c, p));
      const most = (i) => (S[i].available ? Math.min(MAX, S[i].most ?? MAX) : 0);
      const pickOk = () => FILL && st.pick >= 0 && most(st.pick) > 0;
      /* puts `extra` sleeves on `home`, then on the nearest lengths (the size up first), never past a length's stock;
         false when the stock can't take them all */
      const spill = (out, extra, home) => {
        const near = S.map((_, i) => i).sort((a, b) => Math.abs(a - home) - Math.abs(b - home) || b - a);
        for (const i of near) {
          const k = Math.min(extra, Math.max(0, most(i) - out[i]));
          out[i] += k;
          extra -= k;
        }
        return extra === 0;
      };
      /* what a fill tap on deal t gives: all on the picked length, or as many as it has in stock and the rest next to it */
      const fillOf = (t, c) => {
        if (!(t > 1 && t <= MAX && pickOk() && untouched(c, st.pick))) return null;
        const out = zeros();
        return spill(out, t, st.pick) ? out : null;
      };
      const fills = (t, c) => !!fillOf(t, c);

      const ONE = S.filter((x) => x.available).reduce((a, x) => Math.min(a, x.price), Infinity);
      const singleFrom = Number.isFinite(ONE) ? ONE : S[CHEAP[0]].price;
      const dealName = (Q) =>
        Q.n >= BIG
          ? `Buy ${Q.n - Q.f}, get ${Q.f} free`
          : Q.f
            ? cfg.deals[Q.n < R.secondAt ? 1 : 2]
            : `${Q.n === 1 ? 'Single sleeve' : 'Single sleeves'} · full price`;
      const freeNames = (fc) => fc.map((k, i) => (k ? `${k} × ${S[i].label}` : '')).filter(Boolean).join(', ');

      /* ---------- the one state object ---------- */
      const st = {
        /* opens on Buy 4, get 1 free at 1/2/1/1 (Curtis 2026-10-15; offer 2026-10-16); the free-pick flow opens empty */
        c: FLOW ? S.map(() => 0) : PRESETS[R.firstAt].slice(),
        single: false, /* Single chosen: starts at 0, + picks the length */
        seen: false, /* the lengths have been scrolled to or used (the one-time scroll is spent) */
        armAt: 0, /* when the bar's first tap scrolled: a quick second tap adds nothing */
        changed: S.map(() => false), /* rows a deal tap just changed get a short tint */
        reel: false,
        rt: 0,
        busy: '', /* '', 'adding' or 'added' */
        error: false,
        inlineOn: false, /* the in-page Add button is on screen, so the bar hides */
        pick: -1, /* the last length the shopper picked with + (deal card fill) */
        fill: false, /* the summary ("8 × 6–7 ft") shows and the rows fold under "Mix lengths?" */
        mix: false, /* "Mix lengths?" is open */
      };

      const el = {
        lad: q('[data-lad]'),
        rungs: qa('[data-deal]'),
        rungPrices: qa('[data-deal] [data-deal-price]'),
        qh: q('[data-qh]'),
        dm: q('[data-dm]'),
        rows: qa('[data-row]'),
        price: q('[data-price]'),
        inline: q('[data-atc-inline]'),
        err: q('[data-err]'),
        reel: q('[data-reel]'),
        reelOn: q('[data-reel-on]'),
        reelQ: q('[data-reel-q]'),
        reelP: q('[data-reel-p]'),
        reelFit: q('[data-reel-fit]'),
        reelImg: q('.nb2-rimg'),
        reelTypes: qa('[data-reel-type]'),
        bar: q('[data-bar]'),
        barN: q('[data-bar-n]'),
        barLine: q('[data-bar-line]'),
        barTotal: q('[data-bar-total]'),
        barTot: q('[data-bar-tot]'),
        barBtn: q('[data-bar-btn]'),
        pane: q('.nb2-pane'),
        rowsBox: q('[data-rows]'),
        fill: q('[data-fill]'),
        fillSum: q('[data-fill-sum]'),
        fillFree: q('[data-fill-free]'),
        mix: q('[data-mix]'),
        say: q('[data-fill-say]'),
      };
      if (!el.fill) st.fill = false;
      const prevFC = S.map(() => 0);
      /* each row's parts, looked up once */
      const rowEls = el.rows.map((r) => ({
        count: q('[data-count]', r),
        dec: q('[data-act="dec"]', r),
        inc: q('[data-act="inc"]', r),
        nb: q('[data-nb]', r),
        tag: null,
      }));

      /* Tap speed: write only what changed, and change text in place (a new text node or element
         makes the browser restyle the whole page, about 20ms on a slow phone; a text edit does not). */
      const txt = (node, s) => {
        if (!node) return;
        const f = node.firstChild;
        if (f && f.nodeType === 3 && !f.nextSibling) {
          if (f.data !== s) f.data = s;
        } else if (node.textContent !== s) node.textContent = s;
      };
      const setData = (t, v) => {
        if (t.data !== v) t.data = v;
      };
      const attr = (node, k, v) => {
        if (node && node.getAttribute(k) !== v) node.setAttribute(k, v);
      };
      const prop = (node, k, v) => {
        if (node && node[k] !== v) node[k] = v;
      };
      const cls = (node, c) => {
        if (node && node.className !== c) node.className = c;
      };
      /* The changing parts are built once and then only re-classed and re-texted: the dots line keeps
         16 dots (unused ones and the zero state hide by class), the savings line keeps its "!" (shown only
         as a warning) and every row keeps a FREE sticker (hidden while the row has none). */
      const DOTS = 16;
      el.dm.innerHTML = `<span class="nb2-dms" aria-hidden="true">${'<span></span>'.repeat(DOTS)}</span><p class="nb2-dmt"></p>`;
      const dots = Array.from(el.dm.firstElementChild.children);
      const dmText = el.dm.lastElementChild.appendChild(document.createTextNode(''));
      el.price.innerHTML = '<span class="nb2-sv"><i aria-hidden="true">!</i></span>';
      const sv = el.price.firstElementChild;
      const svText = sv.appendChild(document.createTextNode(''));
      el.rows.forEach((r, i) => {
        r.querySelectorAll('.nb2-fl').forEach((t) => t.remove());
        const tag = document.createElement('span');
        tag.className = 'nb2-fl is-off';
        tag.setAttribute('aria-hidden', 'true');
        tag.appendChild(document.createTextNode(''));
        r.prepend(tag);
        rowEls[i].tag = tag;
      });

      const order = () => {
        const Q = quote(st.c);
        const r = st.reel && REELS[st.rt] ? REELS[st.rt] : null;
        return { Q, r, total: Q.total + (r ? r.price : 0) };
      };

      /* ---------- render: state → DOM ---------- */
      const render = () => {
        const O = order();
        const { Q } = O;
        const n = Q.n;
        const zero = n === 0;
        const lit = litFor(n, st.single);

        el.rungs.forEach((b, j) => {
          const k = +b.dataset.deal;
          const on = lit === k;
          b.classList.toggle('is-on', on);
          attr(b, 'aria-checked', on ? 'true' : 'false');
          prop(b, 'tabIndex', on || (!lit && k === R.firstAt) ? 0 : -1);
          /* the ticked row: this order; any other: what a tap on it adds (Single: the cheapest length) */
          const p = el.rungPrices[j];
          if (p) txt(p, $(on && n ? Q.total : k === 1 ? singleFrom : quote(this.fp ? this.fp.target(k) : target(k, st.c)).total));
        });

        /* the dots line above the rows */
        if (zero) {
          cls(el.dm, 'nb2-dm is-zero');
          setData(dmText, cfg.deals[1]);
        } else {
          const nx = nextAt(n);
          const slots = Math.min(
            DOTS,
            n < R.firstAt ? R.firstAt : n < R.secondAt ? R.secondAt : free(n) > free(n - 1) || nx == null ? n : nx,
          );
          dots.forEach((d, j) => {
            const i = j + 1;
            const isFree = FREE_DOT[i];
            const have = i <= n;
            cls(d, i > slots ? 'x' : isFree ? `f${have ? ' got' : ''}` : have ? 'p' : '');
          });
          const toGo = (nx ?? n) - n;
          const title = Q.f
            ? `✓ ${Q.f === 1 ? '1 free sleeve' : `${Q.f} free sleeves`}${n >= BIG ? ` · ${dealName(Q)}` : ''}`
            : toGo === 1
              ? '1 more and it’s free'
              : `${toGo} more for a free sleeve`;
          cls(el.dm, `nb2-dm${Q.f ? ' is-won' : ''}`);
          setData(dmText, title);
        }

        /* the rows, their counts and the moving FREE sticker (on the rows quote().fc names) */
        el.rows.forEach((r, i) => {
          const k = Q.fc[i];
          const R2 = rowEls[i];
          r.classList.toggle('is-zero', !st.c[i]);
          r.classList.toggle('is-free', !!k);
          r.classList.toggle('is-changed', st.changed[i]);
          txt(R2.count, String(st.c[i]));
          prop(R2.dec, 'disabled', !st.c[i]);
          prop(R2.inc, 'disabled', n >= MAX || !S[i].available);
          R2.tag.classList.toggle('is-off', !k);
          if (k) {
            setData(R2.tag.firstChild, `FREE ×${k}`);
            /* the sticker's entrance plays again when its number changes, on the compositor */
            if (prevFC[i] !== k && !reduced() && R2.tag.animate) {
              R2.tag.animate(
                [
                  { opacity: 0, transform: 'rotate(-20deg) scale(1.5)' },
                  { opacity: 1, transform: 'rotate(-4deg)' },
                ],
                { duration: 360, easing: 'cubic-bezier(.2,.9,.3,1.2)', fill: 'backwards' },
              );
            }
          }
          prevFC[i] = k;
          /* "Mix lengths?" open: the picked length and the two next to it are marked (mixes are mostly neighbours) */
          const d = st.fill && st.mix && st.pick >= 0 ? i - st.pick : 9;
          r.classList.toggle('is-near', d === -1 || d === 1);
          r.classList.toggle('is-pick', d === 0);
          txt(R2.nb, d === 0 ? ' · your length' : d === -1 ? ' · size down' : d === 1 ? ' · size up' : '');
        });

        /* the fill summary and "Mix lengths?" */
        if (el.fill) {
          prop(el.fill, 'hidden', !st.fill);
          prop(el.rowsBox, 'hidden', st.fill && !st.mix);
          el.pane.classList.toggle('is-fill', st.fill && !st.mix);
          attr(el.mix, 'aria-expanded', st.fill && st.mix ? 'true' : 'false');
          if (st.fill) {
            txt(el.fillSum, zero ? 'No sleeves yet' : st.c.map((k, i) => (k ? `${k} × ${S[i].label}` : '')).filter(Boolean).join(' + '));
            el.fillFree.classList.toggle('is-off', !Q.f);
            txt(el.fillFree, Q.f ? `FREE ×${Q.f}` : '\u00a0');
            el.fill.classList.toggle('is-changed', st.changed.some(Boolean));
          }
        }

        /* the savings line */
        if (zero) {
          cls(sv, 'nb2-sv is-pick');
          setData(svText, 'Tap + on the length you need');
        } else if (Q.save) {
          /* R3 pick C (Curtis 2026-10-16): "You save $14.99 (18%) + 1 free", all from quote() */
          cls(sv, 'nb2-sv is-deal');
          setData(svText, `You save ${$(Q.save)} (${Math.round((Q.save * 100) / Q.full)}%) + ${Q.f} free`);
        } else {
          const need = R.firstAt - n;
          cls(sv, 'nb2-sv is-warn');
          setData(svText, `Add ${need} more ${need === 1 ? 'sleeve' : 'sleeves'} to get ${R.firstFree} free`);
        }

        cls(el.price, Q.save && !zero ? 'nb2-pl is-deal' : 'nb2-pl');
        /* the in-page Add button: the only add button while it is on screen.
           R3 (Curtis 2026-10-16): a plain "Add to cart", with a deal (C, the deal line above says the saving)
           and without one ("make just add to cart"). */
        txt(
          el.inline,
          st.busy === 'adding'
            ? 'Adding…'
            : st.busy === 'added'
              ? 'Added ✓'
              : zero
                ? 'Choose size'
                : 'Add to cart',
        );
        attr(el.inline, 'aria-busy', st.busy === 'adding' ? 'true' : 'false');
        prop(el.err, 'hidden', !st.error);

        /* Reel Armour */
        if (el.reel && REELS[st.rt]) {
          const r = REELS[st.rt];
          el.reel.classList.toggle('is-on', st.reel);
          prop(el.reelOn, 'checked', st.reel);
          txt(el.reelQ, r.ask);
          txt(el.reelP, $(r.price));
          txt(el.reelFit, r.fit);
          if (el.reelImg && r.img && el.reelImg.getAttribute('src') !== r.img) {
            el.reelImg.setAttribute('src', r.img);
            el.reelImg.removeAttribute('srcset');
          }
          el.reelTypes.forEach((b, i) => {
            b.classList.toggle('is-on', i === st.rt);
            attr(b, 'aria-checked', i === st.rt ? 'true' : 'false');
            prop(b, 'tabIndex', i === st.rt ? 0 : -1);
          });
        }

        /* the two-row bar: "5 sleeves / Buy 4, get 1 free" and "$67.96 total / USD", then one button */
        const one = zero && st.single;
        txt(el.barN, one ? 'Single sleeve' : sl(n));
        txt(
          el.barLine,
          one
            ? 'Tap + on your length'
            : zero
              ? 'Choose your lengths'
              : (Q.f ? dealName(Q) : 'Full price') + (O.r ? ' + reel' : ''),
        );
        txt(el.barTotal, one ? `${$(S[CHEAP[0]].price)}+` : $(O.total));
        prop(el.barTot, 'hidden', one);
        txt(
          el.barBtn,
          st.busy === 'adding'
            ? 'Adding…'
            : st.busy === 'added'
              ? 'Added ✓'
              : one
                ? 'Choose size ›'
                : zero || !st.seen
                  ? cfg.barChoose
                  : 'Add to cart' /* the total is beside it in the bar (Curtis 2026-10-16: "make just add to cart") */,
        );
        el.bar.classList.toggle('is-away', st.inlineOn);
        /* while the bar shows, the footer gets room under it so the bar never covers its last lines,
           and Tab keeps focus above it (a class on <html>: an html:has() rule restyled the page on every tap) */
        document.documentElement.classList.toggle('ra-nb2-bar-on', !st.inlineOn);
        attr(el.bar, 'aria-hidden', st.inlineOn ? 'true' : 'false');
        prop(el.barBtn, 'tabIndex', st.inlineOn ? -1 : 0);
      };

      /* ---------- actions: change state, then render() ---------- */
      const toLengths = (force) => {
        if (st.seen && !force) return;
        if (!st.seen) st.armAt = Date.now();
        st.seen = true;
        const y = el.qh.getBoundingClientRect().top + window.pageYOffset - 12;
        try {
          window.scrollTo({ top: y, behavior: reduced() ? 'auto' : 'smooth' });
        } catch {
          window.scrollTo(0, y);
        }
        try {
          el.qh.focus({ preventScroll: true });
        } catch {}
      };

      const chooseDeal = (t) => {
        const before = st.c.slice();
        st.single = t === 1;
        st.error = false;
        const filled = fills(t, st.c);
        /* the summary stays while the shopper's own mix is topped up on the picked length; Single or a fallback ends it */
        st.fill = !!el.fill && (filled || (st.fill && t !== 1 && pickOk()));
        if (!st.fill) st.mix = false;
        st.c = target(t, st.c);
        if (filled && el.say) {
          /* read out once per fill (emptied first, so the same words are read again) */
          const got = st.c.map((k, i) => (k ? `${k} × ${S[i].label}` : '')).filter(Boolean);
          const words =
            st.c[st.pick] === t ? `${t} sleeves, all ${S[st.pick].label}` : `${t} sleeves: ${got.join(', ')}. Only ${st.c[st.pick]} ${S[st.pick].label} left`;
          txt(el.say, '');
          clearTimeout(this.sayT);
          this.sayT = setTimeout(() => txt(el.say, words), 80);
        }
        st.changed = st.c.map((v, i) => v !== before[i]);
        clearTimeout(this.flashT);
        this.flashT = setTimeout(() => {
          st.changed = S.map(() => false);
          render();
        }, 900);
      };

      const step = (i, dir) => {
        st.seen = true;
        st.error = false;
        if (dir > 0) {
          if (sum(st.c) < MAX && S[i].available) {
            st.c[i]++;
            /* the picked length; + inside "Mix lengths?" moves sleeves across and keeps the pick */
            if (!st.fill) st.pick = i;
          }
        } else if (st.c[i]) st.c[i]--;
      };

      const add = async () => {
        if (st.busy || !sum(st.c)) return;
        const items = st.c.map((quantity, i) => ({ id: S[i].id, quantity })).filter((x) => x.quantity > 0);
        const O = order();
        if (O.r) items.push({ id: O.r.id, quantity: 1 });
        st.busy = 'adding';
        st.error = false;
        render();
        try {
          await new Promise((resolve, reject) => {
            const t = setTimeout(() => reject(new Error('timeout')), 15000);
            this.dispatchEvent(
              new CustomEvent('ra:cart-add', {
                bubbles: true,
                detail: {
                  items,
                  source: 'ra-nb2-buybox',
                  resolve: (v) => (clearTimeout(t), resolve(v)),
                  reject: (e) => (clearTimeout(t), reject(e)),
                },
              }),
            );
          });
          st.busy = 'added';
          render();
          clearTimeout(this.doneT);
          this.doneT = setTimeout(() => {
            st.busy = '';
            render();
          }, 1600);
        } catch {
          st.busy = '';
          st.error = true;
          render();
        }
      };

      this.addEventListener('click', (e) => {
        const t = e.target instanceof Element ? e.target : null;
        if (!t) return;
        let x;
        /* any tap on the deals, lengths, size chart or reel add-on means the shopper has engaged:
           from then on the bar reads "Add to cart" (Curtis 2026-10-16) */
        /* (deal taps and +/- already set it, the deal tap also doing its one-time scroll) */
        if (!st.seen && t.closest('.nb2-fit, [data-reel-type], [data-reel-on]')) {
          st.seen = true;
          st.armAt = Date.now();
          render();
        }
        if ((x = t.closest('[data-deal]'))) {
          /* free pick: a deal tap opens the sheet (Single works as before) */
          if (this.fp && this.fp.deal(+x.dataset.deal, x)) return;
          chooseDeal(+x.dataset.deal);
          toLengths(); /* one-time scroll to the lengths on the first deal tap */
          render();
          return;
        }
        if ((x = t.closest('[data-act]'))) {
          if (x.disabled) return;
          step(+x.dataset.i, x.dataset.act === 'inc' ? 1 : -1);
          render();
          /* free pick: the + that reaches a free sleeve opens the sheet */
          if (this.fp && x.dataset.act === 'inc') this.fp.step(x);
          return;
        }
        if (t.closest('[data-mix]')) {
          st.mix = !st.mix;
          render();
          return;
        }
        if (t.closest('[data-atc-inline]')) {
          if (sum(st.c)) add();
          else {
            if (st.fill) st.mix = true;
            render();
            toLengths(true);
          }
          return;
        }
        if (t.closest('[data-bar-btn]')) {
          if (!st.seen) {
            toLengths();
            render();
            return;
          }
          if (Date.now() - st.armAt < 700) return; /* a quick second tap after the scroll adds nothing */
          if (sum(st.c)) add();
          else {
            if (st.fill) st.mix = true;
            render();
            toLengths(true);
          }
          return;
        }
        if ((x = t.closest('[data-reel-type]'))) {
          st.rt = +x.dataset.reelType;
          render();
        }
      });
      el.reelOn?.addEventListener('change', (e) => {
        st.reel = e.target.checked;
        render();
      });

      /* radio groups: arrow keys move and choose (the deals, and the reel type) */
      const arrows = (group, sel) =>
        group?.addEventListener('keydown', (e) => {
          const dir = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[e.key];
          if (!dir) return;
          const items = qa(sel, group);
          const i = items.indexOf(document.activeElement);
          if (i < 0) return;
          e.preventDefault();
          const next = items[(i + dir + items.length) % items.length];
          next.focus();
          next.click();
        });
      arrows(el.lad, '[data-deal]');
      arrows(q('[data-reel-types]'), '[data-reel-type]');

      /* the bar shows only while the in-page Add button is off screen */
      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver((entries) => {
          const on = entries[entries.length - 1].isIntersecting;
          if (on !== st.inlineOn) {
            st.inlineOn = on;
            render();
          }
        });
        this.io.observe(el.inline);
      }

      /* the room the footer keeps for the bar: its height plus its bottom gap */
      if ('ResizeObserver' in window) {
        this.ro = new ResizeObserver(() => {
          const h = el.bar.offsetHeight;
          if (h) document.documentElement.style.setProperty('--nb2-bar-room', `${h + 16}px`);
        });
        this.ro.observe(el.bar);
      }

      /* "Find my size" in the menu links to #ra-size-guide: open the fit table and bring it into view */
      const fitTable = q('#ra-size-guide');
      const openFit = () => {
        if (!fitTable || window.location.hash !== '#ra-size-guide') return;
        fitTable.open = true;
        fitTable.scrollIntoView({ block: 'center' });
      };
      openFit();
      window.addEventListener('hashchange', openFit);

      /* the free-pick sheet's result: its counts become the order, folded into the summary as a deal fill does */
      const apply = (c) => {
        const before = st.c.slice();
        st.c = c.slice();
        st.single = false;
        st.error = false;
        st.seen = true;
        st.armAt = 0;
        st.fill = !!el.fill;
        st.mix = false;
        if (st.pick < 0 || !st.c[st.pick]) st.pick = st.c.indexOf(Math.max(...st.c));
        st.changed = st.c.map((v, i) => v !== before[i]);
        clearTimeout(this.flashT);
        this.flashT = setTimeout(() => {
          st.changed = S.map(() => false);
          render();
        }, 900);
        render();
      };

      this.gallery();
      render();
      this.nb2 = { state: st, quote, free, render, target, cfg, S, CHEAP, money: $, most, apply, inline: el.inline };
    }

    /* gallery: native scroll; the script keeps the counter in step, drives the arrows and loads the video on tap */
    gallery() {
      const gv = this.querySelector('[data-gal]');
      if (!gv) return;
      const slides = Array.from(gv.querySelectorAll('[data-slide]'));
      const gn = this.querySelector('[data-gnum]');
      const gb = Array.from(this.querySelectorAll('[data-gdir]'));
      const dots = Array.from(this.querySelectorAll('[data-gp] i'));
      const tiles = Array.from(this.querySelectorAll('[data-tile]'));
      const row = this.querySelector('[data-tiles]');
      const N = slides.length;

      /* thumbnails 5+ load when they come into the row's view (Liquid gives them data-src) */
      if (row && 'IntersectionObserver' in window) {
        const tio = new IntersectionObserver((es) => es.forEach((e) => {
          if (!e.isIntersecting) return;
          const im = e.target;
          if (im.dataset.srcset) im.srcset = im.dataset.srcset;
          im.src = im.dataset.src;
          im.removeAttribute('data-srcset');
          im.removeAttribute('data-src');
          tio.unobserve(im);
        }), { root: row, rootMargin: '0px 72px 0px 0px' });
        row.querySelectorAll('img[data-src]').forEach((im) => tio.observe(im));
      } else if (row) row.querySelectorAll('img[data-src]').forEach((im) => { if (im.dataset.srcset) im.srcset = im.dataset.srcset; im.src = im.dataset.src; });
      /* Review 2026-10-22: Tab met every slide's opener and every thumbnail (~22 stops) before the lengths. Now only the
         photo on show is in the Tab order, the thumbnails are one stop (arrow keys move along them, Enter / Space
         picks), and each opener says which photo it opens. */
      const FOCUSABLE = 'button, a[href], video[controls], [tabindex]';
      const tabOrder = (i) => {
        slides.forEach((sl, j) => sl.querySelectorAll(FOCUSABLE).forEach((el) => (el.tabIndex = j === i ? 0 : -1)));
        tiles.forEach((t, j) => (t.tabIndex = j === i ? 0 : -1));
        /* Codex batch round 1: focus on a photo that just slid away follows to the photo on show (it stayed on the
           off-screen opener) */
        const a = document.activeElement;
        /* only a control inside a slide (focus on the scrolling gallery itself stays there: Codex batch round 2) */
        if (a && slides.some((sl) => sl.contains(a)) && slides[i] && !slides[i].contains(a)) {
          const to = slides[i].querySelector(FOCUSABLE);
          if (to instanceof HTMLElement) to.focus({ preventScroll: true });
        }
      };
      slides.forEach((sl, j) => {
        const open = sl.querySelector('.nb2-open');
        if (open && open.getAttribute('aria-label') === 'Open photo gallery') open.setAttribute('aria-label', `Open photo ${j + 1} of ${N}`);
      });
      if (row) {
        row.addEventListener('keydown', (e) => {
          const dir = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : e.key === 'Home' ? -N : e.key === 'End' ? N : 0;
          const at = e.target instanceof Element ? tiles.indexOf(/** @type {HTMLElement} */ (e.target.closest('[data-tile]'))) : -1;
          if (!dir || at < 0) return;
          e.preventDefault();
          const to = Math.max(0, Math.min(tiles.length - 1, at + dir));
          tiles.forEach((t, j) => (t.tabIndex = j === to ? 0 : -1));
          tiles[to].focus();
        });
      }
      tabOrder(0);
      let last = 0;
      let pending = false;
      /* one step = one photo plus the gap (under 990px the photos are narrower than the row: the next one peeks in) */
      const step = () => (N > 1 ? slides[1].offsetLeft - slides[0].offsetLeft : gv.clientWidth) || 1;
      const idx = () => Math.max(0, Math.min(N - 1, Math.round(gv.scrollLeft / step())));
      /* Off-screen slides carry data-src (Liquid): the file loads when a slide is next to the one on show,
         when the shopper touches the gallery, taps a tile or an arrow, or once the page is idle (the second photo). */
      const woken = new Set();
      const wake = (i) => {
        if (i < 0 || i >= N || woken.has(i)) return;
        woken.add(i);
        slides[i].querySelectorAll('img[data-src]').forEach((im) => {
          if (im.dataset.srcset) im.srcset = im.dataset.srcset;
          im.src = im.dataset.src;
          im.removeAttribute('data-srcset');
          im.removeAttribute('data-src');
        });
      };
      const near = (i) => {
        wake(i);
        wake(i + 1);
        wake(i - 1);
      };
      ['touchstart', 'pointerdown', 'focusin'].forEach((t) => gv.addEventListener(t, () => near(idx()), { passive: true }));
      /* a finger on the gallery takes over from a tile/arrow jump still in flight */
      ['touchstart', 'pointerdown'].forEach((t) => gv.addEventListener(t, () => cancelJump(), { passive: true }));
      /* Phones (the peek layout): at idle photo 2 shows its thumbnail's file (already loaded, so no extra bytes), enough
         for the 28px that peeks in; the full file loads when the shopper comes near it. Desktop shows one photo, so nothing loads at idle (image bytes at start
         no higher than before, Curtis 2026-10-17). */
      const peek = () => {
        const im = slides[1] && slides[1].querySelector('img[data-src]');
        const th = tiles[1] && tiles[1].querySelector('img');
        if (im && th && !woken.has(1) && window.matchMedia('(max-width: 989px)').matches) im.src = th.currentSrc || th.src;
      };
      const idle = () => (window.requestIdleCallback ? requestIdleCallback(peek, { timeout: 3000 }) : setTimeout(peek, 1500));
      if (document.readyState === 'complete') idle();
      else window.addEventListener('load', idle, { once: true });
      let heading = -1; /* the slide a go() is scrolling to (-1: none); the sweep below waits until it arrives */
      let headingT = 0;
      /* a video on a slide other than the one on screen stops (fleet review 2026-10-22) */
      const sweep = () => {
        const i = idx();
        slides.forEach((sl, j) => {
          if (j !== i) sl.querySelectorAll('video').forEach((v) => !v.paused && v.pause());
        });
      };
      /* Codex round 2: a jump that is cut short (finger, or it never arrives) must still stop the video it left behind */
      const cancelJump = () => {
        if (heading < 0) return;
        heading = -1;
        sweep();
      };
      const go = (i) => {
        heading = i;
        clearTimeout(headingT);
        headingT = setTimeout(cancelJump, 1500); /* never stuck if the scroll is interrupted */
        near(i);
        const left = i * step();
        try {
          gv.scrollTo({ left, behavior: reduced() ? 'auto' : 'smooth' });
        } catch {
          gv.scrollLeft = left;
        }
      };
      const sync = () => {
        pending = false;
        const i = idx();
        near(i);
        const arrived = heading < 0 || i === heading;
        if (i === heading) heading = -1;
        if (i === last) {
          if (arrived) sweep();
          return;
        }
        if (gb[0]) gb[0].disabled = i === 0;
        if (gb[1]) gb[1].disabled = i === N - 1;
        last = i;
        tabOrder(i);
        /* fleet review 2026-10-22: a video on another slide stops (it played on, with sound, off screen), as in the Reel
           and kit galleries. Codex round 1: not while a tile/arrow jump is still passing the slides in between (that
           paused the video it was going to); the sweep runs when the jump arrives, or on a swipe. */
        if (arrived) sweep();
        tiles.forEach((t, j) => t.setAttribute('aria-current', j === i ? 'true' : 'false'));
        /* keep the active tile in view inside its own row (never scrolls the page) */
        const t = tiles[i];
        if (t && row && row.scrollWidth > row.clientWidth) {
          const r = t.getBoundingClientRect();
          const rr = row.getBoundingClientRect();
          if (r.left < rr.left + 8 || r.right > rr.right - 8) {
            const left = row.scrollLeft + r.left - rr.left - (rr.width - r.width) / 2;
            try {
              row.scrollTo({ left, behavior: reduced() ? 'auto' : 'smooth' });
            } catch {
              row.scrollLeft = left;
            }
          }
        }
        if (!gn) return;
        gn.innerHTML = `<b class="is-up">${i + 1}</b> / ${N}`;
        dots.forEach((d, j) => (d.className = j < i ? 'is-done' : j === i ? 'is-on' : ''));
      };
      gv.addEventListener(
        'scroll',
        () => {
          if (!pending) {
            pending = true;
            requestAnimationFrame(sync);
          }
        },
        { passive: true },
      );
      gb.forEach((b) => b.addEventListener('click', () => go(idx() + +b.dataset.gdir)));
      /* phones play the ≤720p mp4: Liquid only knows the 1080p one, so look the rendition up in the product JSON
         (one small request, on the first tap only; on a slow or failed lookup the 1080p file plays) */
      const phone = () => window.matchMedia('(max-width: 989px)').matches;
      let renditions = null;
      const smallSrc = (id) => {
        renditions =
          renditions ||
          Promise.race([
            fetch(this.cfg.productJs, { credentials: 'same-origin' }).then((r) => (r.ok ? r.json() : null)),
            new Promise((resolve) => setTimeout(resolve, 2000, null)),
          ]).catch(() => null);
        return renditions.then((p) => {
          const m = p?.media?.find((x) => String(x.id) === String(id));
          const ok = (m?.sources || []).filter((x) => x.format === 'mp4' && x.height <= 720);
          ok.sort((a, b) => b.height - a.height);
          return ok[0]?.url || '';
        });
      };
      /* Curtis 2026-10-21: the video plays right where it is, in its gallery slide (no full-screen viewer, whose backdrop
         showed as dark bars around the card); the play button and the video tile both call play(). */
      const watch = (slide) => play(slide);
      /* the ≤720p lookup is warmed once the page is idle, so the first tap starts the video at once */
      const vMedia = this.querySelector('[data-video-tpl]')?.content?.querySelector('video[data-media]')?.dataset.media;
      if (vMedia && phone()) (window.requestIdleCallback || ((f) => setTimeout(f, 1500)))(() => smallSrc(vMedia));
      const play = async (slide) => {
        /* back on a video the gallery paused (fleet review 2026-10-22): the video tile / Watch resumes it */
        if (slide.classList.contains('is-playing')) {
          const on = slide.querySelector('video');
          if (on && on.paused) on.play().catch(() => {});
          return;
        }
        const tpl = slide.querySelector('[data-video-tpl]');
        if (!tpl) return;
        slide.classList.add('is-playing');
        const frag = tpl.content.cloneNode(true);
        /* always muted: a cloned muted attribute does not mute, so set it before the video goes in */
        const v = frag.querySelector('video');
        if (v) {
          v.removeAttribute('autoplay');
          v.defaultMuted = true;
          v.muted = true;
          v.playsInline = true;
          const source = v.querySelector('source');
          if (source && v.dataset.media && phone()) {
            const url = await smallSrc(v.dataset.media);
            if (url) source.src = url;
            /* Codex round 1: the lookup can take a moment; if the shopper has moved to another photo meanwhile, the
               video does not start off screen (the Play button stays, for when they come back) */
            const at = slides.indexOf(slide);
            if (at >= 0 && idx() !== at && heading !== at) {
              slide.classList.remove('is-playing');
              return;
            }
          }
        }
        /* fleet review 2026-10-22: started from the keyboard, focus moves onto the video (the Play button it replaces is
           gone, so focus fell to the page and Space scrolled it); Space / Enter then pause and play the video */
        const hadFocus = slide.contains(document.activeElement);
        slide.replaceChildren(frag);
        if (v) window.raPlayMuted ? window.raPlayMuted(v) : v.play?.().catch(() => {});
        if (v && hadFocus) {
          if (!v.hasAttribute('controls') && !v.hasAttribute('tabindex')) v.tabIndex = 0;
          if (!v.hasAttribute('controls')) {
            v.addEventListener('keydown', (e) => {
              if (e.key !== ' ' && e.key !== 'Enter') return;
              e.preventDefault();
              if (v.paused) v.play().catch(() => {});
              else v.pause();
            });
          }
          /* the muted-video helper's Play/Pause button (ra-muted-video.js) when it adds one, else the video */
          requestAnimationFrame(() => (slide.querySelector('.ra-vpp') || v).focus({ preventScroll: true }));
        }
      };
      this.addEventListener('click', (e) => {
        const t = e.target instanceof Element ? e.target : null;
        if (!t) return;
        const p = t.closest('[data-play]');
        if (p) play(p.closest('[data-slide]'));
        const tile = t.closest('[data-tile]');
        if (tile) {
          const k = +tile.dataset.tile;
          go(k);
          if (slides[k]?.hasAttribute('data-video-slide')) watch(slides[k]);
        }
        if (t.closest('[data-watch]')) {
          const vs = this.querySelector('[data-video-slide]');
          if (!vs) return;
          go(slides.indexOf(vs));
          watch(vs);
        }
      });
    }
  }

  customElements.define('ra-nb2-buybox', RaNb2Buybox);
})();
